import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

type Accent = 'brand' | 'success' | 'warning' | 'error' | 'neutral';

const accentStyles: Record<
  Accent,
  { iconBg: string; iconText: string; ring: string }
> = {
  brand: {
    iconBg: 'bg-brand-50 dark:bg-brand-500/10',
    iconText: 'text-brand-600 dark:text-brand-400',
    ring: 'hover:ring-brand-500/20',
  },
  success: {
    iconBg: 'bg-success-50 dark:bg-success-500/10',
    iconText: 'text-success-600 dark:text-success-400',
    ring: 'hover:ring-success-500/20',
  },
  warning: {
    iconBg: 'bg-warning-50 dark:bg-warning-500/10',
    iconText: 'text-warning-600 dark:text-warning-500',
    ring: 'hover:ring-warning-500/20',
  },
  error: {
    iconBg: 'bg-error-50 dark:bg-error-500/10',
    iconText: 'text-error-600 dark:text-error-400',
    ring: 'hover:ring-error-500/20',
  },
  neutral: {
    iconBg: 'bg-gray-100 dark:bg-gray-800',
    iconText: 'text-gray-700 dark:text-gray-300',
    ring: 'hover:ring-gray-500/10',
  },
};

export interface DashboardStatCardProps {
  label: string;
  value: string | number;
  link: string;
  icon: ReactNode;
  accent?: Accent;
}

export default function DashboardStatCard({
  label,
  value,
  link,
  icon,
  accent = 'neutral',
}: DashboardStatCardProps) {
  const styles = accentStyles[accent];

  return (
    <Link
      to={link}
      className={`group block rounded-2xl border border-gray-200 bg-white p-5 ring-1 ring-transparent transition-all hover:border-gray-300 hover:shadow-md dark:border-gray-800 dark:bg-white/[0.03] dark:hover:border-gray-700 md:p-6 ${styles.ring}`}
    >
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-xl ${styles.iconBg}`}
      >
        <span className={`${styles.iconText} [&>svg]:size-6`}>{icon}</span>
      </div>
      <p className="mt-5 text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 font-bold text-gray-800 text-title-sm dark:text-white/90">
        {value}
      </p>
      <span className="mt-3 inline-flex items-center text-xs font-medium text-brand-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-brand-400">
        View details →
      </span>
    </Link>
  );
}
